import { z } from 'zod';
import type { CheckoutStatus } from './billing.contract';

/**
 * The Giving service's public contract.
 *
 * A donation references a donor by Person id, resolved through PeopleReadPort
 * at read time — the same rule every dependent module follows. `donorPersonId`
 * is nullable: an anonymous or cash-drop gift is a real, recordable donation
 * with nobody to attribute it to.
 */

export const DONATION_FUNDS = ['GENERAL', 'MISSIONS', 'BUILDING', 'BENEVOLENCE', 'OTHER'] as const;
export type DonationFund = (typeof DONATION_FUNDS)[number];

export const DONATION_METHODS = ['CASH', 'CHECK', 'CARD', 'ACH', 'ONLINE'] as const;
export type DonationMethod = (typeof DONATION_METHODS)[number];

export const DONATION_STATUSES = ['RECORDED', 'PENDING', 'FAILED', 'REFUNDED'] as const;
export type DonationStatus = (typeof DONATION_STATUSES)[number];

export const recordDonationRequestSchema = z.object({
  donorPersonId: z.uuid().nullish(),
  fund: z.enum(DONATION_FUNDS),
  /** Whole currency units are rejected — amounts are always cents. */
  amount: z.number().int().positive(),
  currency: z.string().trim().length(3).toUpperCase().default('USD'),
  method: z.enum(DONATION_METHODS),
  checkNumber: z.string().trim().max(50).nullish(),
  /** ISO datetime. Defaults to now when omitted — a Sunday count keyed in later still needs it set explicitly. */
  receivedAt: z.iso.datetime().nullish(),
  isTaxDeductible: z.boolean().default(true),
  note: z.string().trim().max(500).nullish(),
  batchLabel: z.string().trim().max(200).nullish(),
  campusId: z.uuid().nullish(),
});
export type RecordDonationRequest = z.infer<typeof recordDonationRequestSchema>;

export const updateDonationRequestSchema = z.object({
  fund: z.enum(DONATION_FUNDS).nullish(),
  amount: z.number().int().positive().nullish(),
  method: z.enum(DONATION_METHODS).nullish(),
  checkNumber: z.string().trim().max(50).nullish(),
  note: z.string().trim().max(500).nullish(),
  batchLabel: z.string().trim().max(200).nullish(),
});
export type UpdateDonationRequest = z.infer<typeof updateDonationRequestSchema>;

export const listDonationsQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  donorPersonId: z.uuid().nullish(),
  fund: z.enum(DONATION_FUNDS).nullish(),
  method: z.enum(DONATION_METHODS).nullish(),
  dateFrom: z.iso.datetime().nullish(),
  dateTo: z.iso.datetime().nullish(),
});
export type ListDonationsQuery = z.infer<typeof listDonationsQuerySchema>;

// --- Public self-service giving -----------------------------------------------

/**
 * A donor giving through the public, unauthenticated `/give/:tenantSlug`
 * page — no session, so no `donorPersonId` to attribute the gift to.
 * `donorName`/`donorEmail` are the giver's own words, carried on the
 * `Donation` row itself rather than resolved against `Person`.
 */
export const publicCheckoutRequestSchema = z.object({
  fund: z.enum(DONATION_FUNDS),
  amount: z.number().int().positive(),
  currency: z.string().trim().length(3).toUpperCase().default('USD'),
  donorName: z.string().trim().max(200).nullish(),
  donorEmail: z.email().trim().toLowerCase().nullish(),
});
export type PublicCheckoutRequest = z.infer<typeof publicCheckoutRequestSchema>;

export interface PublicCheckoutResponse {
  readonly status: CheckoutStatus;
  /** Redirect the donor here for a `PENDING` checkout; null once `SUCCEEDED`
   *  (the stub provider, or a zero-amount edge case) — there is nothing left
   *  to redirect to. */
  readonly checkoutUrl: string | null;
}

/** What the public giving page needs to render before a donor decides anything. */
export interface PublicTenantInfoResponse {
  readonly name: string;
  readonly defaultCurrency: string;
  readonly funds: readonly DonationFund[];
}

// --- Recurring giving ---------------------------------------------------------

/**
 * Not an auto-charge — no card is stored. This starts a standing reminder:
 * `RecurringGivingReminderJob` emails a fresh one-time checkout link every
 * `frequency`, and the donor decides whether to click it. See the
 * `RecurringGivingSchedule` entity's own doc comment for why.
 */
export const RECURRING_GIVING_FREQUENCIES = ['WEEKLY', 'MONTHLY'] as const;
export type RecurringGivingFrequency = (typeof RECURRING_GIVING_FREQUENCIES)[number];

export const RECURRING_GIVING_STATUSES = ['ACTIVE', 'PAUSED', 'CANCELLED'] as const;
export type RecurringGivingStatus = (typeof RECURRING_GIVING_STATUSES)[number];

export const createRecurringGivingRequestSchema = z.object({
  fund: z.enum(DONATION_FUNDS),
  amount: z.number().int().positive(),
  currency: z.string().trim().length(3).toUpperCase().default('USD'),
  frequency: z.enum(RECURRING_GIVING_FREQUENCIES),
  donorName: z.string().trim().max(200).nullish(),
  /** Never optional here, unlike a one-time gift's — there is nowhere to
   *  send the next reminder without it. */
  donorEmail: z.email().trim().toLowerCase(),
});
export type CreateRecurringGivingRequest = z.infer<typeof createRecurringGivingRequestSchema>;

export interface RecurringGivingScheduleResponse {
  readonly status: RecurringGivingStatus;
  readonly fund: DonationFund;
  readonly amount: number;
  readonly currency: string;
  readonly frequency: RecurringGivingFrequency;
  /** ISO date of the next reminder email — null once cancelled, since there
   *  will not be one. */
  readonly nextRunAt: string | null;
  /** The same link the confirmation email carries, shown on-screen too —
   *  a donor who closes the tab still has one more chance to find it
   *  before relying on their inbox. */
  readonly manageUrl: string;
}

// --- Responses --------------------------------------------------------------

export interface DonationResponse {
  readonly id: string;
  readonly donorPersonId: string | null;
  readonly donorDisplayName: string | null;
  readonly donorName: string | null;
  readonly donorEmail: string | null;
  readonly fund: DonationFund;
  readonly amount: number;
  readonly currency: string;
  readonly method: DonationMethod;
  readonly provider: string | null;
  readonly externalId: string | null;
  readonly checkNumber: string | null;
  readonly status: DonationStatus;
  readonly receivedAt: string;
  readonly isTaxDeductible: boolean;
  readonly note: string | null;
  readonly batchLabel: string | null;
  readonly campusId: string | null;
  readonly createdAt: string;
}
